// @ts-nocheck
/**
 * Custom Product Menu — Web Component
 *
 * Desktop: glassmorphic dropdown that replaces the default mega-menu submenu
 *          for a specific nav item matched by title.
 * Mobile:  bottom-sheet menu that intercepts the hamburger drawer.
 */
class CustomProductMenu extends HTMLElement {
  constructor() {
    super();
    this._desktopOpen = false;
    this._mobileOpen = false;
    this._triggerTitle = (this.dataset.triggerTitle || 'bestsellers').toLowerCase();
    this._desktopPanel = null;
    this._mobileSheet = null;
    this._mobileOverlay = null;
    this._closeBtn = null;
    this._desktopHideTimer = null;
    this._triggerLink = null;
    this._triggerListItem = null;
    this._headerMenu = null;
    this._headerDrawer = null;
    this._hammerSummary = null;
  }

  connectedCallback() {
    this.removeAttribute('hidden');

    this._desktopPanel = this.querySelector('[data-cpm-desktop]');
    this._mobileSheet = this.querySelector('[data-cpm-mobile]');
    this._mobileOverlay = this.querySelector('[data-cpm-overlay]');
    this._closeBtn = this.querySelector('[data-cpm-close]');

    // Wait for the header-menu custom element to fully upgrade before patching
    this._waitForHeaderMenu();
    this._setupMobile();
    this._setupEscapeKey();
  }

  disconnectedCallback() {
    if (this._onEscape) document.removeEventListener('keydown', this._onEscape);
    if (this._onDocumentClick) document.removeEventListener('click', this._onDocumentClick);
    if (this._onPointerMoveDesktop) document.removeEventListener('pointermove', this._onPointerMoveDesktop);
    // Remove the panel from body if it was moved there
    if (this._desktopPanel && this._desktopPanel.parentElement === document.body) {
      document.body.removeChild(this._desktopPanel);
    }
  }

  /* =========================================
     DESKTOP — wait for header-menu to be ready
     ========================================= */

  _waitForHeaderMenu() {
    var self = this;

    function trySetup() {
      var headerMenu = document.querySelector('header-menu');
      if (!headerMenu) return false;

      // Wait until the theme sets data-menu-style on #header-component
      var headerComponent = document.getElementById('header-component');
      if (!headerComponent || !headerComponent.hasAttribute('data-menu-style')) return false;

      self._setupDesktop(headerMenu);
      return true;
    }

    if (!trySetup()) {
      var headerComponent = document.getElementById('header-component');
      if (headerComponent) {
        var observer = new MutationObserver(function () {
          if (trySetup()) observer.disconnect();
        });
        observer.observe(headerComponent, { attributes: true });
      } else {
        setTimeout(function () { self._waitForHeaderMenu(); }, 200);
      }
    }
  }

  /* =========================================
     DESKTOP
     ========================================= */

  _setupDesktop(headerMenu) {
    var self = this;
    this._headerMenu = headerMenu;

    var menuLinks = headerMenu.querySelectorAll('.menu-list__link');
    var triggerLink = null;
    var triggerListItem = null;

    menuLinks.forEach(function (link) {
      var titleEl = link.querySelector('.menu-list__link-title');
      if (titleEl && titleEl.textContent.trim().toLowerCase() === self._triggerTitle) {
        triggerLink = link;
        triggerListItem = link.closest('.menu-list__list-item');
      }
    });

    if (!triggerLink || !triggerListItem) return;

    this._triggerLink = triggerLink;
    this._triggerListItem = triggerListItem;

    // --- Step 1: Disconnect the theme's hover system from this item entirely ---
    // The theme registers capture-phase document listeners for on:pointerenter / on:pointerleave.
    // Removing these attributes means the theme's activate() / deactivate() will NEVER fire
    // for this <li>, so the header underlay and --submenu-height logic is fully bypassed.
    triggerListItem.removeAttribute('on:pointerenter');
    triggerListItem.removeAttribute('on:pointerleave');
    triggerListItem.removeAttribute('on:focus');
    triggerListItem.removeAttribute('on:blur');

    // --- Step 2: Remove the default submenu DOM node (belt & suspenders) ---
    var submenu = triggerListItem.querySelector('.menu-list__submenu');
    if (submenu) submenu.remove();

    // Clean up now-stale aria attrs on the link
    triggerLink.removeAttribute('aria-controls');
    triggerLink.removeAttribute('aria-haspopup');

    // --- Step 3: Attach the panel to document.body ---
    // Reasons to use body (not header-component):
    // (a) overflow-list shadow DOM has overflow-y:hidden — clips <li> children
    // (b) .header has `contain: layout style` which makes position:fixed children
    //     fixed relative to the header, not the viewport — breaking backdrop-filter
    // Appending to body gives us a true viewport-fixed element with working backdrop-filter.
    document.body.appendChild(this._desktopPanel);

    // --- Step 4: Position the panel under the trigger <li> ---
    // Small visual gap below the nav bar, shifted 24px left of the trigger item.
    var PANEL_GAP = 8; // visual gap between the nav bar and the panel
    this._panelGap = PANEL_GAP;
    this._positionPanel = function () {
      var rect = triggerListItem.getBoundingClientRect();
      self._desktopPanel.style.top = (rect.bottom + PANEL_GAP) + 'px';
      self._desktopPanel.style.left = (rect.left - 24) + 'px';
      // Keep the legacy CSS bridge sized too, as belt-and-suspenders.
      self._desktopPanel.style.setProperty('--cpm-bridge-height', (PANEL_GAP + 12) + 'px');
    };

    // --- Step 5: Bulletproof hover intent ---
    // Instead of relying on pointerenter/leave hit-testing (which a CSS bridge can
    // miss when the header's own stacking context intercepts the pointer in the gap),
    // we track the pointer on the document while the panel is open and keep it open
    // whenever the cursor is inside the trigger, the panel, OR the gap band between
    // them. The menu only closes once the cursor is genuinely outside that region.
    var EDGE_TOLERANCE = 10; // px of forgiveness around each rect
    var HIDE_DELAY = 200; // ms grace before closing once outside the hot zone

    function inflatedRectHit(x, y, r, m) {
      return x >= r.left - m && x <= r.right + m && y >= r.top - m && y <= r.bottom + m;
    }

    this._isPointerInHotZone = function (x, y) {
      var t = triggerListItem.getBoundingClientRect();
      var p = self._desktopPanel.getBoundingClientRect();
      if (inflatedRectHit(x, y, t, EDGE_TOLERANCE)) return true;
      if (inflatedRectHit(x, y, p, EDGE_TOLERANCE)) return true;
      // Gap band: anything vertically between the trigger and the panel, within the
      // combined horizontal span of the two — this is the path the cursor travels.
      var bandTop = Math.min(t.bottom, p.top) - EDGE_TOLERANCE;
      var bandBottom = Math.max(t.bottom, p.top) + EDGE_TOLERANCE;
      var bandLeft = Math.min(t.left, p.left) - EDGE_TOLERANCE;
      var bandRight = Math.max(t.right, p.right) + EDGE_TOLERANCE;
      return y >= bandTop && y <= bandBottom && x >= bandLeft && x <= bandRight;
    };

    this._onPointerMoveDesktop = function (e) {
      if (!self._desktopOpen) return;
      if (self._isPointerInHotZone(e.clientX, e.clientY)) {
        clearTimeout(self._desktopHideTimer);
        self._desktopHideTimer = null;
      } else if (!self._desktopHideTimer) {
        self._desktopHideTimer = setTimeout(function () { self._hideDesktop(); }, HIDE_DELAY);
      }
    };

    triggerListItem.addEventListener('pointerenter', function () {
      clearTimeout(self._desktopHideTimer);
      self._desktopHideTimer = null;
      self._positionPanel();
      self._showDesktop();
    });

    // Reposition while open so the hot zone math stays accurate on scroll/resize.
    window.addEventListener('scroll', function () { if (self._desktopOpen) self._positionPanel(); }, { passive: true });
    window.addEventListener('resize', function () { if (self._desktopOpen) self._positionPanel(); });

    // Click on trigger link: navigate to the link's URL (hover handles the dropdown)
    triggerLink.addEventListener('click', function (e) {
      var href = triggerLink.getAttribute('href');
      if (href && href !== '#' && href !== '') {
        // Let the browser navigate naturally
        return;
      }
      // Only toggle panel if no real URL is set
      e.preventDefault();
      self._desktopOpen ? self._hideDesktop() : self._showDesktop();
    });

    // Close on any outside click
    this._onDocumentClick = function (e) {
      if (!self._desktopOpen) return;
      if (triggerListItem.contains(e.target) || (self._desktopPanel && self._desktopPanel.contains(e.target))) return;
      self._hideDesktop();
    };
    document.addEventListener('click', this._onDocumentClick);
  }

  _showDesktop() {
    if (!this._desktopPanel) return;
    this._desktopOpen = true;
    this._desktopPanel.classList.add('cpm-desktop-panel--open');
    if (this._triggerLink) this._triggerLink.classList.add('cpm-link--open');
    // Start tracking the pointer so we can keep the panel open across the gap.
    if (this._onPointerMoveDesktop) {
      document.addEventListener('pointermove', this._onPointerMoveDesktop);
    }
  }

  _hideDesktop() {
    if (!this._desktopPanel) return;
    this._desktopOpen = false;
    clearTimeout(this._desktopHideTimer);
    this._desktopHideTimer = null;
    this._desktopPanel.classList.remove('cpm-desktop-panel--open');
    if (this._triggerLink) this._triggerLink.classList.remove('cpm-link--open');
    if (this._onPointerMoveDesktop) {
      document.removeEventListener('pointermove', this._onPointerMoveDesktop);
    }
  }

  /* =========================================
     MOBILE
     ========================================= */

  _setupMobile() {
    var self = this;
    var headerDrawer = document.querySelector('header-drawer');
    if (!headerDrawer) return;

    var summary = headerDrawer.querySelector('summary');
    if (!summary) return;

    this._headerDrawer = headerDrawer;
    this._hammerSummary = summary;

    // Capture phase — runs before the theme's own click handler
    summary.addEventListener('click', function (e) {
      if (window.innerWidth >= 750) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      self._mobileOpen ? self._hideMobile() : self._showMobile();
    }, true);

    if (this._closeBtn) {
      this._closeBtn.addEventListener('click', function () { self._hideMobile(); });
    }

    if (this._mobileOverlay) {
      this._mobileOverlay.addEventListener('click', function () { self._hideMobile(); });
    }

    this._setupDragToDismiss();
  }

  _showMobile() {
    var self = this;
    this._mobileOpen = true;

    // Prevent the theme's hamburger from showing close icon
    this._forceDrawerClosed();

    document.body.style.overflow = 'hidden';
    if (this._mobileOverlay) this._mobileOverlay.classList.add('cpm-mobile-overlay--open');

    // Double rAF: sheet is painted at translateY(100%) first, then transition fires
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        if (self._mobileSheet) self._mobileSheet.classList.add('cpm-mobile-sheet--open');
      });
    });
  }

  _hideMobile() {
    this._mobileOpen = false;

    if (this._mobileSheet) {
      this._mobileSheet.classList.remove('cpm-mobile-sheet--open');
      this._mobileSheet.style.transform = '';
    }
    if (this._mobileOverlay) this._mobileOverlay.classList.remove('cpm-mobile-overlay--open');

    document.body.style.overflow = '';
    this._forceDrawerClosed();
  }

  /**
   * Ensure the theme's header-drawer <details> is always in closed state
   * so the hamburger shows the open (menu) icon, never the close (X) icon.
   */
  _forceDrawerClosed() {
    var headerDrawer = this._headerDrawer;
    if (!headerDrawer) return;

    var details = headerDrawer.querySelector('details');
    if (!details) return;

    var isOpen = details.hasAttribute('open') || details.classList.contains('menu-open');
    if (!isOpen) return;

    // Use the theme component's own close method to cleanly reset its internal state
    var themeClose = headerDrawer.close;
    if (typeof themeClose === 'function') {
      themeClose.call(headerDrawer);
    } else {
      details.classList.remove('menu-open');
      details.removeAttribute('open');
      var s = details.querySelector('summary');
      if (s) s.setAttribute('aria-expanded', 'false');
    }
  }

  _setupDragToDismiss() {
    var self = this;
    var sheet = this._mobileSheet;
    var handle = sheet ? sheet.querySelector('.cpm-mobile-sheet__handle-bar') : null;
    if (!handle || !sheet) return;

    var startY = 0;
    var currentY = 0;

    handle.addEventListener('touchstart', function (e) {
      startY = e.touches[0].clientY;
      currentY = startY;
      sheet.style.transition = 'none';
    }, { passive: true });

    handle.addEventListener('touchmove', function (e) {
      currentY = e.touches[0].clientY;
      var diff = currentY - startY;
      if (diff < 0) sheet.style.transform = 'translateY(' + diff + 'px)';
    }, { passive: true });

    handle.addEventListener('touchend', function () {
      sheet.style.transition = '';
      var diff = currentY - startY;
      if (diff < -100) {
        self._hideMobile();
      } else {
        sheet.style.transform = '';
      }
      startY = 0;
      currentY = 0;
    });
  }

  /* =========================================
     SHARED
     ========================================= */

  _setupEscapeKey() {
    var self = this;
    this._onEscape = function (e) {
      if (e.key !== 'Escape') return;
      self._hideDesktop();
      if (self._mobileOpen) self._hideMobile();
    };
    document.addEventListener('keydown', this._onEscape);
  }
}

if (!customElements.get('custom-product-menu')) {
  customElements.define('custom-product-menu', CustomProductMenu);
}
